import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CircleDollarSign, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingTab = ({ token }) => {
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaultSize, setDefaultSize] = useState('24x36');
  const [settingDefault, setSettingDefault] = useState(false);
  const [shippingRate, setShippingRate] = useState('5.99');
  const [freeThreshold, setFreeThreshold] = useState('50');
  const [savingShipping, setSavingShipping] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/pricing`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/admin/pricing/default-size`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/admin/shipping-config`, { headers: { Authorization: `Bearer ${token}` } })
    ]).then(([pRes, dRes, sRes]) => {
      setSizes(pRes.data.sizes);
      setDefaultSize(dRes.data.default_size);
      setShippingRate(String(sRes.data.flat_rate ?? '5.99'));
      setFreeThreshold(String(sRes.data.free_shipping_threshold ?? '50'));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const handleSaveShipping = async () => {
    setSavingShipping(true);
    try {
      await axios.put(`${API}/admin/shipping-config`, {
        flat_rate: parseFloat(shippingRate),
        free_shipping_threshold: parseFloat(freeThreshold)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Shipping settings saved!');
    } catch { toast.error('Failed to save shipping settings'); }
    finally { setSavingShipping(false); }
  };

  const updateSizePrice = (index, newPrice) => {
    setSizes(prev => prev.map((s, i) => i === index ? { ...s, price: newPrice } : s));
  };

  const addSize = () => {
    setSizes(prev => [...prev, { size: '', price: 0 }]);
  };

  const removeSize = (index) => {
    setSizes(prev => prev.filter((_, i) => i !== index));
  };

  const updateSizeName = (index, newName) => {
    setSizes(prev => prev.map((s, i) => i === index ? { ...s, size: newName } : s));
  };

  const handleSave = async () => {
    const valid = sizes.every(s => s.size && s.price >= 0);
    if (!valid) { toast.error('All sizes need a name and valid price'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/pricing`, sizes.map(s => ({ size: s.size, price: parseFloat(s.price) })), { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      toast.success(`Pricing updated for ${res.data.updated.toLocaleString()} products!`);
      setSizes(res.data.sizes);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update pricing'); }
    finally { setSaving(false); }
  };

  const handleSetDefaultSize = async (sizeName) => {
    setSettingDefault(true);
    try {
      const res = await axios.put(`${API}/admin/pricing/default-size`, { size: sizeName }, { headers: { Authorization: `Bearer ${token}` } });
      setDefaultSize(sizeName);
      toast.success(res.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to set default'); }
    finally { setSettingDefault(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-pricing">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Pricing by Size</h2>
          <p className="text-sm text-gray-500">Set prices for each print size. Changes apply to all {'\u2248'}43K products.</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="px-6 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40 flex items-center gap-2"
          data-testid="save-pricing-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CircleDollarSign className="w-4 h-4" />}
          {saving ? 'Applying...' : 'Save & Apply to All Products'}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">#</th>
              <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Print Size</th>
              <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Price (USD)</th>
              <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">Preview</th>
              <th className="text-right p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">Remove</th>
            </tr>
          </thead>
          <tbody>
            {sizes.map((s, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`size-row-${i}`}>
                <td className="p-4 text-sm text-gray-400 font-mono">{i + 1}</td>
                <td className="p-4">
                  <input type="text" value={s.size}
                    onChange={(e) => updateSizeName(i, e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded text-sm text-gray-900 w-40 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="e.g. 24x36"
                    data-testid={`size-name-${i}`} />
                </td>
                <td className="p-4">
                  <div className="relative w-36">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input type="number" step="0.01" min="0" value={s.price}
                      onChange={(e) => updateSizePrice(i, e.target.value)}
                      className="pl-7 pr-3 py-2 border border-gray-200 rounded text-sm text-gray-900 w-full font-semibold focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      data-testid={`size-price-${i}`} />
                  </div>
                </td>
                <td className="p-4">
                  <div className="bg-gray-100 rounded px-3 py-1.5 text-center">
                    <span className="text-xs text-gray-500">{s.size}"</span>
                    <span className="block text-sm font-bold text-gray-900">${parseFloat(s.price || 0).toFixed(2)}</span>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => removeSize(i)}
                    className="p-1.5 rounded text-gray-300 hover:text-red-600 hover:bg-red-50 transition-colors"
                    data-testid={`remove-size-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-4 border-t border-gray-100">
          <button onClick={addSize}
            className="px-4 py-2 border border-dashed border-gray-300 text-gray-500 text-sm rounded hover:border-gray-900 hover:text-gray-900 transition-colors flex items-center gap-2"
            data-testid="add-size-btn">
            <Plus className="w-4 h-4" /> Add Size
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Default Display Price</h3>
            <p className="text-xs text-gray-500 mt-0.5">Choose which size price is shown as the main price across the store.</p>
          </div>
          {settingDefault && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        </div>
        <div className="flex flex-wrap gap-2" data-testid="default-size-selector">
          {sizes.map((s) => (
            <button key={s.size} onClick={() => handleSetDefaultSize(s.size)}
              disabled={settingDefault}
              className={`px-4 py-2.5 rounded border text-sm font-semibold transition-all ${
                defaultSize === s.size
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
              } disabled:opacity-50`}
              data-testid={`default-size-${s.size}`}>
              {s.size}" — ${parseFloat(s.price || 0).toFixed(2)}
              {defaultSize === s.size && <span className="ml-1.5 text-xs opacity-70">(active)</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded p-4 text-sm text-yellow-800">
        <strong>Note:</strong> Saving will update the price for every product in your catalog. The default display price determines which size price visitors see first.
      </div>

      {/* Shipping Settings */}
      <div className="bg-white border border-gray-200 rounded p-5" data-testid="shipping-settings">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Shipping Costs</h3>
            <p className="text-xs text-gray-500 mt-0.5">Set flat-rate shipping and free shipping threshold for the entire store.</p>
          </div>
          <button onClick={handleSaveShipping} disabled={savingShipping}
            className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40 flex items-center gap-2"
            data-testid="save-shipping-btn">
            {savingShipping ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {savingShipping ? 'Saving...' : 'Save Shipping'}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Flat Rate Shipping</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input type="number" step="0.01" min="0" value={shippingRate}
                onChange={(e) => setShippingRate(e.target.value)}
                className="pl-7 pr-3 py-2.5 border border-gray-200 rounded text-sm text-gray-900 w-full font-semibold focus:outline-none focus:ring-2 focus:ring-gray-900"
                data-testid="shipping-rate-input" />
            </div>
            <p className="text-[11px] text-gray-400 mt-1">Charged when order is below the free threshold</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Free Shipping Over</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input type="number" step="1" min="0" value={freeThreshold}
                onChange={(e) => setFreeThreshold(e.target.value)}
                className="pl-7 pr-3 py-2.5 border border-gray-200 rounded text-sm text-gray-900 w-full font-semibold focus:outline-none focus:ring-2 focus:ring-gray-900"
                data-testid="free-threshold-input" />
            </div>
            <p className="text-[11px] text-gray-400 mt-1">Orders at or above this amount get free shipping</p>
          </div>
        </div>
      </div>
    </div>
  );
};


export default PricingTab;
